/**
 * Seletor de "acompanhante do dia": força vínculo a um paciente cadastrado
 * (diferente do PatientLinkField, que aceita texto livre). Não persiste no
 * cadastro permanente do paciente — vive no SessionDraft de uma consulta.
 *
 * Estados:
 *   - "vazio": input de busca; só fixa quando o usuário clica num resultado.
 *   - "completar": o paciente escolhido não tem os dados mínimos do
 *     acompanhante (nome completo, CPF e telefone) — o vínculo só acontece
 *     depois de preencher o que falta (AccompanistDataForm grava na ficha).
 *   - "vinculado": chip com nome + atalho pra abrir a ficha do acompanhante
 *     + botão pra trocar (volta ao input). Se o vínculo veio de antes da regra
 *     (ou de outra tela) e está incompleto, o chip acusa e oferece completar.
 */

import { useEffect, useRef, useState } from 'react';
import { Users, X, ExternalLink, Search, UserPlus, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PatientService } from '@/services/firebase/patients';
import { PatientMetaLines } from '@/components/patients/PatientMetaLines';
import { AccompanistDataForm } from '@/components/patients/AccompanistDataForm';
import {
  formatMissingFields,
  missingAccompanistData,
  type AccompanistField,
} from '@/utils/accompanistRequirements';
import type { Patient } from '@/types/models';

export interface AccompanistSelectProps {
  clinicId: string | null | undefined;
  selectedId?: string;
  selectedName?: string;
  /** Disparado quando o usuário escolhe um paciente ou limpa o vínculo. */
  onChange: (patient: Patient | null) => void;
  /** ID a excluir da busca (normalmente o próprio paciente da consulta). */
  excludePatientId?: string;
  label?: string;
  /** Variante compacta (menor altura/fonte) — usada na barra do Prontuário. */
  compact?: boolean;
  /** Desabilita a busca (ex.: no agendamento, antes de escolher o paciente). */
  disabled?: boolean;
  /** Placeholder do input quando desabilitado. */
  disabledPlaceholder?: string;
  /** Quando presente, o dropdown ganha o botão "Cadastrar acompanhante"
   *  (no vazio e no rodapé dos resultados) — chamado com o texto buscado,
   *  pra pré-preencher o cadastro rápido. */
  onRegisterNew?: (query: string) => void;
}

export function AccompanistSelect({
  clinicId,
  selectedId,
  selectedName,
  onChange,
  excludePatientId,
  label = 'Acompanhante do dia',
  compact = false,
  disabled = false,
  disabledPlaceholder = 'Selecione o paciente primeiro',
  onRegisterNew,
}: AccompanistSelectProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Patient[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  /** Paciente escolhido que ainda não tem os mínimos — segura o vínculo até o
   *  AccompanistDataForm gravar o que falta. */
  const [pending, setPending] = useState<{ patient: Patient; missing: AccompanistField[] } | null>(
    null,
  );
  /** Lacunas do acompanhante JÁ vinculado (dado legado ou vindo de outra tela). */
  const [linkedGaps, setLinkedGaps] = useState<
    { patient: Patient; missing: AccompanistField[] } | null
  >(null);
  const [fixingLinked, setFixingLinked] = useState(false);

  /** Vincula se estiver completo; senão abre o complemento obrigatório. */
  const selectPatient = (p: Patient) => {
    const missing = missingAccompanistData(p);
    if (missing.length === 0) {
      onChange(p);
      setQuery('');
      setResults(null);
      setOpen(false);
      setPending(null);
      return;
    }
    setPending({ patient: p, missing });
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (selectedId) return;
    if (!clinicId) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const digitsOnly = trimmed.replace(/\D/g, '');
        const looksLikeCpf = digitsOnly.length >= 2 && /^[\d.\-/]+$/.test(trimmed);
        const mode = looksLikeCpf ? 'cpf' : 'name';
        const res = await PatientService.searchPatients(clinicId, trimmed, mode);
        const filtered = excludePatientId
          ? res.filter((p) => p.id !== excludePatientId)
          : res;
        setResults(filtered);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, clinicId, excludePatientId, selectedId]);

  // Audita o vínculo já existente: acompanhantes gravados antes desta regra
  // (ou por outra tela) podem estar sem CPF/telefone — o chip acusa e deixa
  // completar ali mesmo, sem abrir a ficha.
  useEffect(() => {
    if (!selectedId || !clinicId) {
      setLinkedGaps(null);
      setFixingLinked(false);
      return;
    }
    setPending(null);
    setFixingLinked(false);
    let cancelled = false;
    void (async () => {
      try {
        const p = await PatientService.getPatient(clinicId, selectedId);
        if (cancelled) return;
        if (!p) {
          setLinkedGaps(null);
          return;
        }
        const full = { ...p, id: selectedId };
        const missing = missingAccompanistData(full);
        setLinkedGaps(missing.length > 0 ? { patient: full, missing } : null);
      } catch {
        if (!cancelled) setLinkedGaps(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, clinicId]);

  if (selectedId) {
    return (
      <div className="space-y-2">
        {label && (
          <label className="block text-xs uppercase font-bold tracking-wide text-on-surface-variant mb-1">
            {label}
          </label>
        )}
        <div
          className={`flex items-center rounded-lg bg-primary-container/40 border border-primary/20 ${
            compact ? 'gap-1.5 px-2 py-1' : 'gap-2 px-3 py-2'
          }`}
        >
          <Users size={compact ? 13 : 14} className="text-primary shrink-0" />
          <div
            className={`rounded-full bg-primary text-on-primary flex items-center justify-center font-bold shrink-0 ${
              compact ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10px]'
            }`}
          >
            {(selectedName || '?').charAt(0).toUpperCase()}
          </div>
          <div className={`flex-1 text-on-surface truncate ${compact ? 'text-[13px]' : 'text-sm'}`}>
            {selectedName || 'Acompanhante vinculado'}
          </div>
          <button
            type="button"
            onClick={() => navigate(`/pacientes/${selectedId}`)}
            className="text-on-surface-variant hover:text-primary transition-colors"
            aria-label="Abrir ficha do acompanhante"
            title="Abrir ficha"
          >
            <ExternalLink size={compact ? 13 : 14} />
          </button>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              setResults(null);
            }}
            className="text-on-surface-variant hover:text-error transition-colors"
            aria-label="Remover acompanhante"
            title="Remover acompanhante"
          >
            <X size={compact ? 13 : 14} />
          </button>
        </div>

        {/* Vínculo incompleto (legado ou criado em outra tela): acusa o que
            falta e completa sem sair da consulta. */}
        {linkedGaps && !fixingLinked && (
          <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={13} className="text-warning shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] text-on-surface">
                Acompanhante sem {formatMissingFields(linkedGaps.missing)} — obrigatório.
              </p>
              <button
                type="button"
                onClick={() => setFixingLinked(true)}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                Completar agora
              </button>
            </div>
          </div>
        )}
        {linkedGaps && fixingLinked && (
          <AccompanistDataForm
            clinicId={clinicId}
            patient={linkedGaps.patient}
            missing={linkedGaps.missing}
            onCompleted={(updated) => {
              setLinkedGaps(null);
              setFixingLinked(false);
              // Re-vincula com os dados novos (nome/CPF entram na sessão e nos
              // documentos que usam o acompanhante).
              onChange(updated);
            }}
            onCancel={() => setFixingLinked(false)}
          />
        )}
      </div>
    );
  }

  // Complemento obrigatório antes de fixar o vínculo.
  if (pending) {
    return (
      <div className="space-y-2">
        {label && (
          <label className="block text-xs uppercase font-bold tracking-wide text-on-surface-variant mb-1">
            {label}
          </label>
        )}
        <AccompanistDataForm
          clinicId={clinicId}
          patient={pending.patient}
          missing={pending.missing}
          onCompleted={(updated) => {
            setPending(null);
            setQuery('');
            setResults(null);
            onChange(updated);
          }}
          onCancel={() => setPending(null)}
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-xs uppercase font-bold tracking-wide text-on-surface-variant mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <Search
          size={compact ? 13 : 14}
          className={`absolute top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none ${
            compact ? 'left-2.5' : 'left-3'
          }`}
        />
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (results) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder={
            disabled
              ? disabledPlaceholder
              : compact
                ? 'Buscar por nome ou CPF…'
                : 'Buscar paciente cadastrado por nome ou CPF…'
          }
          autoComplete="off"
          className={`w-full rounded-lg bg-surface text-on-surface border border-outline/30 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60 disabled:cursor-not-allowed ${
            compact ? 'pl-8 pr-3 py-1 text-[13px]' : 'pl-9 pr-3 py-2 text-sm'
          }`}
        />
      </div>

      {!disabled && open && query.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-surface border border-outline-variant/20 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-xs text-on-surface-variant animate-pulse">
              Buscando pacientes…
            </div>
          )}
          {!loading && results && results.length === 0 && (
            onRegisterNew ? (
              <div className="px-4 py-3 space-y-2">
                <p className="text-xs text-on-surface-variant italic">
                  Nenhum paciente encontrado com esse nome/CPF.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onRegisterNew(query.trim());
                  }}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                >
                  <UserPlus size={13} /> Cadastrar acompanhante
                </button>
              </div>
            ) : (
              <div className="px-4 py-3 text-xs text-on-surface-variant italic">
                Nenhum paciente encontrado. Cadastre primeiro pra vincular como acompanhante.
              </div>
            )
          )}
          {!loading && results && results.length > 0 && (
            <ul>
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => selectPatient(p)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-primary/10 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary-container text-primary flex items-center justify-center text-xs font-bold shrink-0">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-on-surface truncate">
                        {p.name}
                      </div>
                      <PatientMetaLines
                        p={p}
                        incomplete={p.registrationStatus === 'MINIMAL'}
                        incompleteLabel="Cadastro incompleto"
                        className="text-[11px] text-on-surface-variant truncate"
                      />
                      {/* Avisa ANTES do clique o que vai ser cobrado pra
                          vincular — nome completo, CPF e telefone. */}
                      {(() => {
                        const gaps = missingAccompanistData(p);
                        return gaps.length > 0 ? (
                          <div className="text-[10px] uppercase font-bold text-warning truncate">
                            Falta {formatMissingFields(gaps)}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <Users size={14} className="text-primary shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* Rodapé com cadastro rápido mesmo COM resultados — o acompanhante
              pode ser homônimo de alguém já cadastrado. */}
          {!loading && results && results.length > 0 && onRegisterNew && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onRegisterNew(query.trim());
              }}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border-t border-outline-variant/20 text-primary text-xs font-medium hover:bg-primary/10 transition-colors"
            >
              <UserPlus size={13} /> Não achou? Cadastrar acompanhante
            </button>
          )}
        </div>
      )}
    </div>
  );
}
